import React, { useState } from "react";
import { Card, Row, Col, Accordion, Badge, Button, Modal, Form } from "react-bootstrap";
import Content from "../../../Components/Dashboard/Content/Content";
import { 
  BookOpen, 
  TrendingUp, 
  Shield, 
  DollarSign, 
  BarChart3, 
  Target,
  AlertTriangle,
  CheckCircle,
  Info,
  Lightbulb,
  Zap,
  Users,
  Settings,
  FileText,
  PlayCircle,
  Download
} from "lucide-react";

const Tutorials = () => {
  const [activeSection, setActiveSection] = useState("algo-trading");
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [riskData, setRiskData] = useState({
    accountBalance: '',
    riskPercentage: '',
    entryPrice: '',
    stopLoss: ''
  });
  const [riskResult, setRiskResult] = useState(null);

  const handleRiskInputChange = (field, value) => {
    setRiskData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateRisk = () => {
    const { accountBalance, riskPercentage, entryPrice, stopLoss } = riskData;
    
    if (!accountBalance || !riskPercentage || !entryPrice || !stopLoss) {
      return;
    }

    const balance = parseFloat(accountBalance);
    const riskPercent = parseFloat(riskPercentage);
    const entry = parseFloat(entryPrice);
    const stop = parseFloat(stopLoss);

    if (isNaN(balance) || isNaN(riskPercent) || isNaN(entry) || isNaN(stop)) {
      return;
    }

    const riskAmount = (balance * riskPercent) / 100;
    const priceDifference = Math.abs(entry - stop);
    const positionSize = riskAmount / priceDifference;
    const riskRewardRatio = entry > stop ? (entry - stop) / priceDifference : (stop - entry) / priceDifference;

    setRiskResult({
      accountBalance: balance,
      riskPercentage: riskPercent,
      riskAmount: riskAmount,
      positionSize: positionSize,
      priceDifference: priceDifference,
      riskRewardRatio: riskRewardRatio,
      entryPrice: entry,
      stopLoss: stop
    });
  };

  const resetRiskCalculator = () => {
    setRiskData({
      accountBalance: '',
      riskPercentage: '',
      entryPrice: '',
      stopLoss: ''
    });
    setRiskResult(null);
  };

  const sections = [
    {
      id: "algo-trading",
      title: "Algorithmic Trading Guide",
      icon: <TrendingUp className="text-primary" size={24} />,
      color: "primary"
    },
    {
      id: "crypto-basics",
      title: "Crypto Trading Basics",
      icon: <DollarSign className="text-success" size={24} />,
      color: "success"
    },
    {
      id: "risk-management",
      title: "Risk Management",
      icon: <Shield className="text-warning" size={24} />,
      color: "warning"
    },
    {
      id: "broker-setup",
      title: "Broker Setup Guide",
      icon: <Settings className="text-info" size={24} />,
      color: "info"
    }
  ];

  const algoTradingContent = [
    {
      title: "What is Algorithmic Trading?",
      content: `Algorithmic trading (algo trading) is the use of computer programs and systems to execute trades automatically based on predefined criteria. Instead of manually placing trades, algorithms analyze market data and execute trades when specific conditions are met.

Key Benefits:
• Eliminates emotional decision-making
• Executes trades 24/7 without human intervention
• Processes large amounts of data quickly
• Maintains consistent trading discipline
• Reduces transaction costs through efficient execution`,
      type: "info"
    },
    {
      title: "How Our Platform Works",
      content: `Our SmartAlgoPanel provides a comprehensive algorithmic trading solution:

1. Strategy Selection: Choose from pre-built trading strategies
2. Broker Integration: Connect your exchange accounts (Binance, CoinDCX, Delta Exchange, CoinSwitch)
3. Risk Parameters: Set stop-loss, take-profit, and position sizing
4. Automated Execution: The system monitors markets and executes trades automatically
5. Performance Tracking: Monitor your trades and strategy performance in real-time

The platform handles all the complex calculations and market analysis, allowing you to focus on strategy selection and risk management.`,
      type: "success"
    },
    {
      title: "Getting Started with Algo Trading",
      content: `Step-by-step guide to start algorithmic trading:

1. Account Setup
   • Create your account and complete verification
   • Fund your trading account with your preferred broker

2. Broker Connection
   • Go to "Connect Your Broker" section
   • Follow the API setup guide for your exchange
   • Test the connection to ensure it's working

3. Strategy Selection
   • Browse available trading strategies
   • Review historical performance and risk metrics
   • Select strategies that match your risk tolerance

4. Risk Management Setup
   • Set maximum position sizes
   • Configure stop-loss and take-profit levels
   • Set daily/weekly loss limits

5. Monitor and Optimize
   • Track performance through the dashboard
   • Adjust parameters based on market conditions
   • Review and learn from trade outcomes`,
      type: "primary"
    }
  ];

  const cryptoBasicsContent = [
    {
      title: "Understanding Cryptocurrency Markets",
      content: `Cryptocurrency markets operate 24/7, unlike traditional stock markets. Key concepts:

Market Structure:
• Spot Trading: Buying and selling actual cryptocurrencies
• Futures Trading: Trading contracts based on future prices
• Margin Trading: Trading with borrowed funds (higher risk)

Price Factors:
• Supply and demand dynamics
• Market sentiment and news
• Regulatory developments
• Technological advancements
• Institutional adoption

Volatility: Crypto markets are highly volatile, with prices can change rapidly. This creates both opportunities and risks.`,
      type: "info"
    },
    {
      title: "Popular Trading Pairs",
      content: `Major cryptocurrency trading pairs:

Bitcoin (BTC):
• BTC/USDT - Most liquid pair
• BTC/USD - Fiat currency pair
• BTC/INR - Indian Rupee pair

Ethereum (ETH):
• ETH/USDT - Second most popular
• ETH/BTC - Against Bitcoin
• ETH/USD - Fiat pair

Other Major Coins:
• BNB/USDT (Binance Coin)
• ADA/USDT (Cardano)
• SOL/USDT (Solana)
• DOGE/USDT (Dogecoin)

Trading Tips:
• Start with major pairs for better liquidity
• Understand the correlation between different assets
• Consider trading fees when selecting pairs`,
      type: "success"
    },
    {
      title: "Market Analysis Techniques",
      content: `Effective trading requires understanding market analysis:

Technical Analysis:
• Chart patterns and trends
• Support and resistance levels
• Moving averages and indicators
• Volume analysis

Fundamental Analysis:
• Project developments and updates
• Market adoption and partnerships
• Regulatory news
• Economic factors

Sentiment Analysis:
• Social media trends
• News sentiment
• Market fear and greed indicators
• Community discussions

Risk Considerations:
• Never invest more than you can afford to lose
• Diversify your portfolio
• Use stop-loss orders
• Keep emotions in check`,
      type: "warning"
    }
  ];

  const riskManagementContent = [
    {
      title: "Position Sizing",
      content: `Proper position sizing is crucial for long-term success:

The 2% Rule:
• Never risk more than 2% of your account on a single trade
• Example: $10,000 account = maximum $200 risk per trade

Position Size Calculation:
Position Size = (Account Balance × Risk Percentage) / (Entry Price - Stop Loss Price)

Example:
• Account: $10,000
• Risk: 2% = $200
• BTC Price: $50,000
• Stop Loss: $48,000 (4% below entry)
• Position Size = $200 / $2,000 = 0.1 BTC

Benefits:
• Protects against large losses
• Allows for multiple trades
• Maintains account longevity
• Reduces emotional stress`,
      type: "primary"
    },
    {
      title: "Stop Loss and Take Profit",
      content: `Essential risk management tools:

Stop Loss Orders:
• Automatically close positions at predetermined loss levels
• Prevents emotional decision-making
• Limits maximum loss per trade
• Should be placed based on technical analysis, not arbitrary percentages

Take Profit Orders:
• Automatically close positions at profit targets
• Locks in gains before market reversal
• Helps maintain trading discipline
• Can be set at resistance levels or profit targets

Trailing Stop Loss:
• Moves stop loss in favor of the trade
• Locks in profits as price moves favorably
• Allows for larger gains while protecting profits
• Useful in trending markets

Best Practices:
• Set stop losses based on market structure
• Use risk-reward ratios of at least 1:2
• Never move stop losses against your position
• Take partial profits at key levels`,
      type: "warning"
    },
    {
      title: "Portfolio Diversification",
      content: `Diversification reduces overall risk:

Asset Diversification:
• Don't put all funds in one cryptocurrency
• Spread across different sectors (DeFi, Layer 1, Gaming, etc.)
• Include both large-cap and small-cap assets
• Consider stablecoins for stability

Strategy Diversification:
• Use multiple trading strategies
• Combine trend-following and mean-reversion strategies
• Include both short-term and long-term approaches
• Test strategies on different timeframes

Correlation Management:
• Avoid highly correlated positions
• Understand how assets move together
• Adjust position sizes based on correlation
• Monitor portfolio correlation regularly

Risk Metrics to Track:
• Maximum Drawdown
• Sharpe Ratio
• Win Rate
• Average Risk-Reward Ratio
• Portfolio Volatility`,
      type: "success"
    },
    {
      title: "Common Risk Management Mistakes",
      content: `Avoid these common pitfalls:

Emotional Trading:
• FOMO (Fear of Missing Out) trades
• Revenge trading after losses
• Overtrading during volatile periods
• Ignoring stop losses

Poor Risk Management:
• Risking too much per trade
• Not using stop losses
• Averaging down without proper analysis
• Chasing losses

Lack of Planning:
• No clear trading plan
• Inconsistent position sizing
• No maximum daily loss limits
• Not reviewing and adjusting strategies

Overconfidence:
• Increasing position sizes after wins
• Ignoring market warnings
• Not adapting to changing conditions
• Believing past performance guarantees future results

Solutions:
• Stick to your trading plan
• Use automated systems
• Regular strategy review
• Continuous education`,
      type: "danger"
    }
  ];

  const brokerSetupContent = [
    {
      title: "Supported Exchanges",
      content: `Our platform supports major cryptocurrency exchanges:

Binance:
• Largest crypto exchange by volume
• Low trading fees
• Wide selection of trading pairs
• Advanced trading features

CoinDCX:
• India's leading crypto exchange
• Compliant with Indian regulations
• INR trading pairs
• User-friendly interface

Delta Exchange:
• Derivatives-focused exchange
• Futures and options trading
• Professional trading tools
• High liquidity

CoinSwitch:
• Multi-exchange aggregator
• Best price discovery
• Simplified trading
• Indian market focus

Choosing the Right Exchange:
• Consider your location and regulations
• Compare trading fees
• Check available trading pairs
• Evaluate security measures
• Review customer support quality`,
      type: "info"
    },
    {
      title: "API Setup Guide",
      content: `Setting up API connections safely:

1. Account Security:
   • Enable 2FA (Two-Factor Authentication)
   • Use strong, unique passwords
   • Regularly update security settings
   • Monitor account activity

2. API Key Creation:
   • Log into your exchange account
   • Navigate to API management section
   • Create new API key
   • Set appropriate permissions

3. API Permissions:
   • Enable: Spot Trading, Futures Trading (if needed)
   • Disable: Withdrawals (for security)
   • Set IP restrictions if possible
   • Use read-only keys for testing

4. Security Best Practices:
   • Never share API keys
   • Use IP whitelisting
   • Regularly rotate API keys
   • Monitor API usage
   • Keep API keys secure

5. Testing Connection:
   • Test with small amounts first
   • Verify order placement works
   • Check balance retrieval
   • Confirm stop-loss functionality`,
      type: "success"
    },
    {
      title: "Troubleshooting Common Issues",
      content: `Common API connection problems and solutions:

Connection Errors:
• Check internet connection
• Verify API key permissions
• Confirm exchange server status
• Check IP restrictions

Authentication Failures:
• Verify API key and secret
• Check timestamp synchronization
• Confirm signature generation
• Review API documentation

Order Placement Issues:
• Check account balance
• Verify trading permissions
• Confirm market hours
• Check minimum order sizes

Balance Retrieval Problems:
• Verify read permissions
• Check account status
• Confirm API key validity
• Review exchange limits

Performance Issues:
• Check API rate limits
• Optimize request frequency
• Use efficient data structures
• Monitor response times

Getting Help:
• Check exchange documentation
• Contact exchange support
• Review our help center
• Join community forums`,
      type: "warning"
    }
  ];

  const getContentBySection = (sectionId) => {
    switch (sectionId) {
      case "algo-trading":
        return algoTradingContent;
      case "crypto-basics":
        return cryptoBasicsContent;
      case "risk-management":
        return riskManagementContent;
      case "broker-setup":
        return brokerSetupContent;
      default:
        return [];
    }
  };

  const getIconByType = (type) => {
    switch (type) {
      case "info":
        return <Info className="text-info" size={20} />;
      case "success":
        return <CheckCircle className="text-success" size={20} />;
      case "warning":
        return <AlertTriangle className="text-warning" size={20} />;
      case "danger":
        return <AlertTriangle className="text-danger" size={20} />;
      case "primary":
        return <Lightbulb className="text-primary" size={20} />;
      default:
        return <FileText className="text-secondary" size={20} />;
    }
  };

  return (
    <Content Page_title="Tutorials & Guides" button_status={false}>
      <div className="tutorials-container">
        {/* Header Section */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <div className="mb-3">
              <BookOpen size={48} className="text-primary" />
            </div>
            <h2 className="mb-3">Tutorials & Guides</h2>
            <p className="text-muted mb-4">
              Master algorithmic trading with our comprehensive guides. Learn crypto basics, 
              risk management, and broker setup to maximize your trading success.
            </p>
            <div className="d-flex justify-content-center  gap-3 flex-wrap">
              <Badge bg="primary" className="px-3 py-2">
                <TrendingUp size={20} className="me-2" />
                Algo Trading
              </Badge>
              <Badge bg="primary" className="px-3 py-2">
                <DollarSign size={20} className="me-2" />
                Crypto Basics
              </Badge>
              <Badge bg="primary" className="px-3 py-2">
                <Shield size={20} className="me-2" />
                Risk Management
              </Badge>
              <Badge bg="primary" className="px-3 py-2">
                <Settings size={20} className="me-2" />
                Broker Setup
              </Badge>
            </div>
          </Card.Body>
        </Card>

        {/* Navigation Tabs */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body className="p-0">
            <div className="nav nav-pills nav-fill" role="tablist">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`nav-link d-flex align-items-center justify-content-center  gap-1 py-3 ${
                    activeSection === section.id ? "active" : ""
                  }`}
                  onClick={() => setActiveSection(section.id)}
                  style={{ borderRadius: 0 }}
                >
                  {section.icon}
                  <span className="d-none d-md-inline">{section.title}</span>
                </button>
              ))}
            </div>
          </Card.Body>
        </Card>

        {/* Content Section */}
        <Row>
          <Col lg={8} md={12}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center mb-4">
                  {sections.find(s => s.id === activeSection)?.icon}
                  <h4 className="mb-0 ms-3">
                    {sections.find(s => s.id === activeSection)?.title}
                  </h4>
                </div>

                <Accordion defaultActiveKey="0" className="tutorial-accordion">
                  {getContentBySection(activeSection).map((item, index) => (
                    <Accordion.Item key={index} eventKey={index.toString()}>
                      <Accordion.Header className="d-flex align-items-center">
                        <div className="d-flex align-items-center">
                          {getIconByType(item.type)}
                          <span className="ms-2">{item.title}</span>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <div className="tutorial-content">
                          {item.content.split('\n').map((line, lineIndex) => {
                            if (line.trim() === '') return <br key={lineIndex} />;
                            
                            if (line.startsWith('•')) {
                              return (
                                <div key={lineIndex} className="d-flex align-items-start mb-2">
                                  <span className="text-primary me-2">•</span>
                                  <span>{line.substring(1).trim()}</span>
                                </div>
                              );
                            }
                            
                            if (line.match(/^\d+\./)) {
                              return (
                                <div key={lineIndex} className="mb-2">
                                  <strong className="text-primary">{line}</strong>
                                </div>
                              );
                            }
                            
                            if (line.includes(':')) {
                              const [label, value] = line.split(':');
                              return (
                                <div key={lineIndex} className="mb-2">
                                  <strong>{label}:</strong> {value}
                                </div>
                              );
                            }
                            
                            return (
                              <p key={lineIndex} className="mb-3">
                                {line}
                              </p>
                            );
                          })}
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4} md={12} className="mt-4 mt-lg-0">
            {/* Quick Tips Sidebar */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0 d-flex align-items-center">
                  <Lightbulb size={20} className="me-2" />
                  Quick Tips
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="quick-tips">
                  <div className="tip-item mb-3">
                    <div className="d-flex align-items-start">
                      <CheckCircle size={16} className="text-success me-2 mt-1" />
                      <div>
                        <strong>Start Small</strong>
                        <p className="mb-0 text-muted small">Begin with small position sizes to learn the platform</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="tip-item mb-3">
                    <div className="d-flex align-items-start">
                      <Shield size={16} className="text-warning me-2 mt-1" />
                      <div>
                        <strong>Always Use Stop Loss</strong>
                        <p className="mb-0 text-muted small">Protect your capital with proper risk management</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="tip-item mb-3">
                    <div className="d-flex align-items-start">
                      <BarChart3 size={16} className="text-info me-2 mt-1" />
                      <div>
                        <strong>Monitor Performance</strong>
                        <p className="mb-0 text-muted small">Track your trades and adjust strategies accordingly</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="tip-item">
                    <div className="d-flex align-items-start">
                      <Users size={16} className="text-primary me-2 mt-1" />
                      <div>
                        <strong>Join Community</strong>
                        <p className="mb-0 text-muted small">Learn from other traders and share experiences</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Resources Card */}
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-success text-white">
                <h5 className="mb-0 d-flex align-items-center">
                  <Download size={20} className="me-2" />
                  Additional Resources
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="resources-list">
                  {/* <div className="resource-item mb-3">
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      className="w-100 d-flex align-items-center justify-content-center"
                      onClick={() => window.open('https://www.youtube.com/watch?v=dQw4w9WgXcQ', '_blank')}
                    >
                      <PlayCircle size={16} className="me-2" />
                      Video Tutorials
                    </Button>
                  </div> */}
                  
                  <div className="resource-item mb-3">
                    <Button 
                      variant="outline-success" 
                      size="sm" 
                      className="w-100 d-flex align-items-center justify-content-center"
                      onClick={() => {
                        // Create and download trading checklist
                        const checklist = `Trading Checklist:
                        
Before Trading:
□ Set up stop loss
□ Check market conditions
□ Review risk parameters
□ Confirm position size
□ Check account balance

During Trading:
□ Monitor positions
□ Watch for news
□ Check technical indicators
□ Review profit/loss
□ Stay disciplined

After Trading:
□ Review performance
□ Analyze mistakes
□ Update strategy
□ Record lessons learned
□ Plan next trades`;
                        
                        const blob = new Blob([checklist], { type: 'text/plain' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'trading-checklist.txt';
                        a.click();
                        window.URL.revokeObjectURL(url);
                      }}
                    >
                      <FileText size={16} className="me-2" />
                      Trading Checklist
                    </Button>
                  </div>
                  
                  <div className="resource-item mb-3">
                    <Button 
                      variant="outline-warning" 
                      size="sm" 
                      className="w-100 d-flex align-items-center justify-content-center"
                      onClick={() => setShowRiskModal(true)}
                    >
                      <Target size={16} className="me-2" />
                      Risk Calculator
                    </Button>
                  </div>
                  
                  <div className="resource-item">
                    <Button 
                      variant="outline-info" 
                      size="sm" 
                      className="w-100 d-flex align-items-center justify-content-center"
                      onClick={() => {
                        const templates = `Strategy Templates:

1. Trend Following Strategy:
   - Use moving averages (20, 50, 200)
   - Buy when price > MA20 > MA50
   - Stop loss: 2% below entry
   - Take profit: 4% above entry

2. Mean Reversion Strategy:
   - Use RSI indicator
   - Buy when RSI < 30 (oversold)
   - Sell when RSI > 70 (overbought)
   - Stop loss: 1.5% below entry

3. Breakout Strategy:
   - Identify support/resistance levels
   - Buy on breakout above resistance
   - Stop loss: below support level
   - Take profit: 2x the risk amount

4. Scalping Strategy:
   - Use 1-minute charts
   - Target small profits (0.5-1%)
   - Quick entries and exits
   - Tight stop losses (0.3%)`;
                        
                        const blob = new Blob([templates], { type: 'text/plain' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'strategy-templates.txt';
                        a.click();
                        window.URL.revokeObjectURL(url);
                      }}
                    >
                      <Zap size={16} className="me-2" />
                      Strategy Templates
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      <style jsx>{`
        .tutorials-container {
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .nav-pills .nav-link {
          border-radius: 0;
          border-bottom: 3px solid transparent;
          transition: all 0.3s ease;
        }
        
        .nav-pills .nav-link:hover {
          opacity: 0.8;
        }
        
        .nav-pills .nav-link.active {
          background-color: transparent;
          border-bottom-color: #007bff;
          color: #007bff;
        }
        
        .tutorial-accordion .accordion-button {
          border: none;
          font-weight: 500;
        }
        
        .tutorial-accordion .accordion-button:not(.collapsed) {
          color: #1976d2;
        }
        
        .tutorial-content {
          line-height: 1.6;
        }
        
        .quick-tips .tip-item {
          padding: 0.5rem 0;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .quick-tips .tip-item:last-child {
          border-bottom: none;
        }
        
        .resource-item button {
          transition: all 0.3s ease;
        }
        
        .resource-item button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        
        @media (max-width: 768px) {
          .nav-pills .nav-link span {
            font-size: 0.8rem;
          }
        }
      `}</style>

      {/* Risk Calculator Modal */}
      <Modal show={showRiskModal} onHide={() => setShowRiskModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <Target size={24} className="me-2 text-warning" />
            Risk Calculator
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6">
              <h6 className="mb-3">Input Parameters</h6>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Account Balance ($)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter account balance"
                    value={riskData.accountBalance}
                    onChange={(e) => handleRiskInputChange('accountBalance', e.target.value)}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Risk Percentage (%)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 2"
                    value={riskData.riskPercentage}
                    onChange={(e) => handleRiskInputChange('riskPercentage', e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Recommended: 1-2% per trade
                  </Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Entry Price ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.000001"
                    placeholder="Enter entry price"
                    value={riskData.entryPrice}
                    onChange={(e) => handleRiskInputChange('entryPrice', e.target.value)}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Stop Loss Price ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.000001"
                    placeholder="Enter stop loss price"
                    value={riskData.stopLoss}
                    onChange={(e) => handleRiskInputChange('stopLoss', e.target.value)}
                  />
                </Form.Group>
                
                <div className="d-flex gap-2">
                  <Button variant="primary" onClick={calculateRisk} className="flex-fill">
                    Calculate Risk
                  </Button>
                  <Button variant="outline-secondary" onClick={resetRiskCalculator}>
                    Reset
                  </Button>
                </div>
              </Form>
            </div>
            
            <div className="col-md-6">
              <h6 className="mb-3">Results</h6>
              {riskResult ? (
                <div className="results-container">
                  <div className="result-item mb-3 p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-semibold">Risk Amount:</span>
                      <span className="text-danger fw-bold">${riskResult.riskAmount.toFixed(2)}</span>
                    </div>
                    <small className="text-muted">
                      {riskResult.riskPercentage}% of ${riskResult.accountBalance.toFixed(2)}
                    </small>
                  </div>
                  
                  <div className="result-item mb-3 p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-semibold">Position Size:</span>
                      <span className="text-primary fw-bold">{riskResult.positionSize.toFixed(6)} units</span>
                    </div>
                    <small className="text-muted">
                      Maximum units to trade
                    </small>
                  </div>
                  
                  <div className="result-item mb-3 p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-semibold">Stop Loss Distance:</span>
                      <span className="text-warning fw-bold">${riskResult.priceDifference.toFixed(2)}</span>
                    </div>
                    <small className="text-muted">
                      Price difference from entry
                    </small>
                  </div>
                  
                  <div className="result-item mb-3 p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-semibold">Risk/Reward Ratio:</span>
                      <span className="text-info fw-bold">1:{riskResult.riskRewardRatio.toFixed(2)}</span>
                    </div>
                    <small className="text-muted">
                      Risk to reward ratio
                    </small>
                  </div>
                  
                  <div className="alert alert-info mt-3">
                    <strong>Summary:</strong><br/>
                    With ${riskResult.accountBalance.toFixed(2)} account balance, risking {riskResult.riskPercentage}% (${riskResult.riskAmount.toFixed(2)}), 
                    you can trade {riskResult.positionSize.toFixed(6)} units at ${riskResult.entryPrice} with stop loss at ${riskResult.stopLoss}.
                  </div>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <Target size={48} className="mb-3 opacity-50" />
                  <p>Fill in the parameters and click "Calculate Risk" to see results</p>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRiskModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Content>
  );
};

export default Tutorials;
